// src/pages/HomePage.js
import React, { useEffect, useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import Skills from '../components/Skills';
import Projects from '../components/Projects';
import Certifications from '../components/Certifications';
import BackgroundMusic from '../components/BackgroundMusic';

// Container
const HomeContainer = styled.div`
    min-height: 100vh;
    background: #0a0a0a;
    color: #ffffff;
    position: relative;
    overflow-x: hidden;
`;

// Hero Section
const HeroSection = styled.section`
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 2rem;
    position: relative;
    background: radial-gradient(ellipse at center, rgba(99, 102, 241, 0.08) 0%, transparent 70%);
`;

const HeroContent = styled.div`
    width: 100%;
    padding: 0 2rem;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 4rem;
    align-items: center;

    @media (max-width: 968px) {
        grid-template-columns: 1fr;
        text-align: center;
        gap: 2rem;
        padding: 0 1rem;
    }
`;

const HeroText = styled.div`
    z-index: 2;
`;

const HeroTitle = styled(motion.h1)`
    font-size: clamp(2.5rem, 5vw, 4rem);
    font-weight: 700;
    line-height: 1.1;
    margin-bottom: 1.5rem;
    
    .gradient-text {
        background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #06b6d4 100%);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
    }
`;

const HeroSubtitle = styled(motion.p)`
    font-size: 1.25rem;
    color: #94a3b8;
    margin-bottom: 2rem;
    line-height: 1.6;
    max-width: 500px;
`;

const CTAContainer = styled(motion.div)`
    display: flex;
    gap: 1rem;
    margin-bottom: 3rem;

    @media (max-width: 640px) {
        flex-direction: column;
    }
`;

const PrimaryButton = styled(motion.button)`
    background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
    color: white;
    border: none;
    padding: 1rem 2rem;
    border-radius: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    font-size: 1rem;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 10px 40px rgba(99, 102, 241, 0.3);
    }
`;

const SecondaryButton = styled(motion.button)`
    background: transparent;
    color: #6366f1;
    border: 2px solid #6366f1;
    padding: 1rem 2rem;
    border-radius: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    font-size: 1rem;

    &:hover {
        background: #6366f1;
        color: white;
        transform: translateY(-2px);
    }
`;

const StatsGrid = styled(motion.div)`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 2rem;

    @media (max-width: 640px) {
        grid-template-columns: 1fr;
    }
`;

const StatItem = styled.div`
    text-align: left;
    
    .number {
        font-size: 2rem;
        font-weight: 700;
        color: #6366f1;
        display: block;
    }
    
    .label {
        color: #64748b;
        font-size: 0.9rem;
        margin-top: 0.5rem;
    }
`;

const HeroVisual = styled(motion.div)`
    position: relative;
    height: 500px;
    display: flex;
    align-items: center;
    justify-content: center;

    @media (max-width: 968px) {
        height: 300px;
    }
`;

const FloatingCard = styled(motion.div)`
    position: absolute;
    background: rgba(255, 255, 255, 0.05);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 16px;
    padding: 1.5rem;
    width: 200px;
    
    &.card-1 {
        top: 10%;
        left: 0;
    }
    
    &.card-2 {
        top: 50%;
        right: 0;
    }
    
    &.card-3 {
        bottom: 10%;
        left: 20%;
    }
`;

// Experience Section
const ExperienceSection = styled.section`
    padding: 8rem 2rem;
    width: 100%;
`;

const SectionTitle = styled(motion.h2)`
    font-size: clamp(2rem, 4vw, 3rem);
    font-weight: 700;
    text-align: center;
    margin-bottom: 1rem;
    background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
`;

const SectionSubtitle = styled(motion.p)`
    text-align: center;
    color: #94a3b8;
    margin-bottom: 4rem;
    font-size: 1.1rem;
    max-width: 600px;
    margin-left: auto;
    margin-right: auto;
`;

const Timeline = styled.div`
    position: relative;
    
    &:before {
        content: '';
        position: absolute;
        left: 2rem;
        top: 0;
        bottom: 0;
        width: 2px;
        background: linear-gradient(180deg, #6366f1, #8b5cf6);
        
        @media (max-width: 768px) {
            left: 1rem;
        }
    }
`;

const TimelineItem = styled(motion.div)`
    position: relative;
    padding-left: 5rem;
    margin-bottom: 3rem;
    
    @media (max-width: 768px) {
        padding-left: 3rem;
    }
    
    &:before {
        content: '';
        position: absolute;
        left: 1.25rem;
        top: 1.5rem;
        width: 1.5rem;
        height: 1.5rem;
        background: #6366f1;
        border-radius: 50%;
        border: 4px solid #0a0a0a;
        
        @media (max-width: 768px) {
            left: 0.25rem;
        }
    }
`;

const ExperienceCard = styled.div`
    background: rgba(255, 255, 255, 0.03);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 16px;
    padding: 2rem;
    transition: all 0.3s ease;
    
    &:hover {
        background: rgba(255, 255, 255, 0.05);
        transform: translateY(-4px);
    }
`;

const ExperienceHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 1rem;
    flex-wrap: wrap;
    gap: 1rem;
`;

const ExperienceTitle = styled.h3`
    font-size: 1.25rem;
    font-weight: 600;
    color: #ffffff;
    margin: 0;
`;

const ExperienceCompany = styled.div`
    color: #6366f1;
    font-weight: 500;
    margin-top: 0.25rem;
`;

const ExperienceDate = styled.div`
    color: #64748b;
    font-size: 0.9rem;
    white-space: nowrap;
`;

const ExperienceList = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0;
`;

const ExperienceBullet = styled.li`
    color: #94a3b8;
    line-height: 1.6;
    margin-bottom: 0.5rem;
    padding-left: 1.5rem;
    position: relative;
    
    &:before {
        content: '→';
        position: absolute;
        left: 0;
        color: #6366f1;
        font-weight: bold;
    }
`;

// About Preview Section
const AboutPreview = styled.section`
    padding: 8rem 2rem;
    background: rgba(255, 255, 255, 0.02);
`;

const AboutContainer = styled.div`
    width: 100%;
    padding: 0 2rem;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 4rem;
    align-items: center;

    @media (max-width: 968px) {
        grid-template-columns: 1fr;
        gap: 2rem;
        padding: 0 1rem;
    }
`;

const AboutText = styled.div``;

const AboutDescription = styled(motion.p)`
    color: #94a3b8;
    line-height: 1.7;
    margin-bottom: 2rem;
    font-size: 1.1rem;
`;

const SkillsPreview = styled(motion.div)`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
`;

const SkillTag = styled.div`
    background: rgba(99, 102, 241, 0.1);
    color: #6366f1;
    padding: 0.5rem 1rem;
    border-radius: 8px;
    font-size: 0.9rem;
    text-align: center;
    border: 1px solid rgba(99, 102, 241, 0.2);
`;

const AboutVisual = styled(motion.div)`
    background: rgba(255, 255, 255, 0.03);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 16px;
    padding: 2rem;
    text-align: center;
`;

const HomePage = () => {
    const containerRef = useRef(null);
    const { scrollYProgress } = useScroll({ target: containerRef });
    const y = useTransform(scrollYProgress, [0, 1], ['0%', '50%']);
    const navigate = useNavigate();

    const experienceData = [
        {
            title: "Graduate Engineering Trainee (GET)",
            company: "HCLtech",
            location: "Chennai",
            date: "09/2025 – Present",
            bullets: [
                "Participating in rigorous onboarding and technical training covering core engineering practices, enterprise-scale software design, and agile methodologies",
                "Engaged in cross-functional projects with exposure to AI/automation, collaborating with senior engineers to deliver scalable technical solutions",
                "Learning and implementing best practices in AI research, digital marketing analytics, automation tools, and data-driven strategy, contributing to ongoing team initiatives, innovation sprints, and experimental marketing campaigns"
            ]
        },
        {
            title: "AI Program Consultant",
            company: "100xEngineers",
            location: "Bangalore, KA, India",
            date: "Apr 2025 – Present",
            bullets: [
                "Architected Generative AI solutions using GPT and BERT, improving trend prediction by 20%",
                "Fine-tuned LLMs for lead scoring and pitch synthesis, boosting response rates by 15%",
                "Contributed to ₹20 Lakh (~$24K) in revenue within 3 months by delivering scalable AI solutions to clients"
            ]
        },
        {
            title: "Database Solutions Engineer",
            company: "The Kite Flyers",
            location: "Noida, UP, India",
            date: "Nov 2024 – Apr 2025",
            bullets: [
                "Optimized SQL and NoSQL databases, increasing query speed by 40% for 10,000+ records",
                "Generated 15 actionable insights from data trend analysis using Python, driving 25% better decision-making",
                "Structured 5 complex datasets, boosting system efficiency by 35%"
            ]
        },
        {
            title: "Software Development and Data Science Intern",
            company: "NIHON Edutech Private Limited",
            location: "Chennai, TN, India",
            date: "Jul 2024 – Aug 2024",
            bullets: [
                "Developed 3 full-stack web applications using HTML5, CSS, JavaScript, Python, and Flask, improving user engagement by 30%",
                "Boosted NLP accuracy by 25% with SpaCy and NLTK, integrating TfidfVectorizer and MultiOutputClassifier",
                "Resolved 95% of system discrepancies, enhancing data accuracy in collaboration with a 5-member team"
            ]
        },
        {
            title: "Lead Researcher",
            company: "Saveetha Medical College",
            location: "Chennai, TN, India",
            date: "May 2024 – Jun 2024",
            bullets: [
                "Led a team of 4 to develop AI cognitive models, achieving 15% faster decision-making with TensorFlow and PyTorch",
                "Analyzed 100+ synaptic connectivity patterns, improving real-time AI performance by 20%",
                "Integrated bio-inspired algorithms, reducing processing time by 10% in 2 practical use cases"
            ]
        }
    ];

    const topSkills = ["AI/ML", "Python", "React", "Node.js", "TensorFlow", "AWS"];

    return (
        <HomeContainer ref={containerRef}>
            {/* Hero Section */}
            <HeroSection>
                <HeroContent>
                    <HeroText>
                        <HeroTitle
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8 }}
                        >
                            Hi, I'm <span className="gradient-text">Udhaya Kumar</span>
                        </HeroTitle>
                        
                        <HeroSubtitle
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                        >
                            Applied AI Engineer building intelligent solutions that solve real-world problems with cutting-edge technology.
                        </HeroSubtitle>
                        
                        <CTAContainer
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.4 }}
                        >
                            <PrimaryButton
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={() => document.getElementById('projects').scrollIntoView({ behavior: 'smooth' })}
                            >
                                View My Work
                            </PrimaryButton>
                            <SecondaryButton
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={() => navigate('/contact')}
                            >
                                Get In Touch
                            </SecondaryButton>
                        </CTAContainer>
                        

                    </HeroText>
                    
                    <HeroVisual
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 1, delay: 0.8 }}
                        style={{ y }}
                    >
                        <FloatingCard
                            className="card-1"
                            animate={{ y: [0, -10, 0] }}
                            transition={{ duration: 3, repeat: Infinity }}
                        >
                            <h4 style={{ color: '#6366f1', marginBottom: '0.5rem' }}>Machine Learning</h4>
                            <p style={{ color: '#94a3b8', fontSize: '0.9rem' }}>Advanced AI models</p>
                        </FloatingCard>
                        
                        <FloatingCard
                            className="card-2"
                            animate={{ y: [0, -15, 0] }}
                            transition={{ duration: 4, repeat: Infinity, delay: 1 }}
                        >
                            <h4 style={{ color: '#8b5cf6', marginBottom: '0.5rem' }}>Full-Stack</h4>
                            <p style={{ color: '#94a3b8', fontSize: '0.9rem' }}>Modern web apps</p>
                        </FloatingCard>
                        
                        <FloatingCard
                            className="card-3"
                            animate={{ y: [0, -12, 0] }}
                            transition={{ duration: 3.5, repeat: Infinity, delay: 2 }}
                        >
                            <h4 style={{ color: '#06b6d4', marginBottom: '0.5rem' }}>Research</h4>
                            <p style={{ color: '#94a3b8', fontSize: '0.9rem' }}>Innovation driven</p>
                        </FloatingCard>
                    </HeroVisual>
                </HeroContent>
            </HeroSection>

            {/* Experience Section */}
            <ExperienceSection>
                <SectionTitle
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    viewport={{ once: true }}
                >
                    Professional Journey
                </SectionTitle>
                <SectionSubtitle
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                    viewport={{ once: true }}
                >
                    Building innovative AI solutions across diverse industries and research domains
                </SectionSubtitle>
                
                <Timeline>
                    {experienceData.map((exp, index) => (
                        <TimelineItem
                            key={index}
                            initial={{ opacity: 0, x: -50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8, delay: index * 0.1 }}
                            viewport={{ once: true }}
                        >
                            <ExperienceCard>
                                <ExperienceHeader>
                                    <div>
                                        <ExperienceTitle>{exp.title}</ExperienceTitle>
                                        <ExperienceCompany>{exp.company} • {exp.location}</ExperienceCompany>
                                    </div>
                                    <ExperienceDate>{exp.date}</ExperienceDate>
                                </ExperienceHeader>
                                <ExperienceList>
                                    {exp.bullets.map((bullet, bulletIndex) => (
                                        <ExperienceBullet key={bulletIndex}>{bullet}</ExperienceBullet>
                                    ))}
                                </ExperienceList>
                            </ExperienceCard>
                        </TimelineItem>
                    ))}
                </Timeline>
            </ExperienceSection>

            {/* About Preview */}
            <AboutPreview>
                <AboutContainer>
                    <AboutText>
                        <SectionTitle
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8 }}
                            viewport={{ once: true }}
                            style={{ textAlign: 'left', marginBottom: '1rem' }}
                        >
                            About Me
                        </SectionTitle>
                        <AboutDescription
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                            viewport={{ once: true }}
                        >
                            I'm passionate about leveraging artificial intelligence to solve complex problems. 
                            With expertise in machine learning, full-stack development, and research, I bridge 
                            the gap between cutting-edge AI research and practical applications.
                        </AboutDescription>
                        <PrimaryButton
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => navigate('/about')}
                        >
                            Learn More About Me
                        </PrimaryButton>
                    </AboutText>
                    
                    <AboutVisual
                        initial={{ opacity: 0, scale: 0.9 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.8, delay: 0.4 }}
                        viewport={{ once: true }}
                    >
                        <h3 style={{ marginBottom: '1.5rem', color: '#ffffff' }}>Core Skills</h3>
                        <SkillsPreview>
                            {topSkills.map((skill, index) => (
                                <SkillTag key={index}>{skill}</SkillTag>
                            ))}
                        </SkillsPreview>
                    </AboutVisual>
                </AboutContainer>
            </AboutPreview>

            {/* Other Sections */}
            <div id="skills">
                <Skills />
            </div>
            <div id="projects">
                <Projects />
            </div>
            <Certifications />
            
            {/* Background Music Player */}
            <BackgroundMusic />
        </HomeContainer>
    );
};

export default HomePage;
